import React, { useEffect, useState } from 'react'
import SubdomainForm from './form'
import SubdomainTable from './table'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'
import { getFromApi, postToApiFile, putToApi } from 'actions'
import { OPERACIONES_SUBDOMINIOS, VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import omit from 'lodash/omit'
import iconsMaterialAPI from '../../../services/IconsMaterialApi'
import { useStateValue } from 'context/store'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'
import { useHasOperation } from 'hooks/useHasOperation'

const INITIAL_SUBDOMAIN_FORM_STATE = {
   id: '',
   name: '',
   description: '',
   status: '',
   icon: '',
   file: '',
   map: ''
}

const TITLE = {
   [VALUES.FORM_BEHAVIOR.REGISTER]: 'Registrar Subdominio',
   [VALUES.FORM_BEHAVIOR.SEARCH]: 'Consultar Subdominio',
   [VALUES.FORM_BEHAVIOR.EDIT]: 'Editar Subdominio'
}

const options = VALUES.STATUS_OPTIONS

const Subdomain = ({ isSearch }) => {
   const [isShowingPopover, setPopoverProp] = useState(true)
   const [iconsList, setIconsList] = useState({}) // do not include in clean()

   const [formData, setFormData] = useState(INITIAL_SUBDOMAIN_FORM_STATE)
   const [formBehavior, setFormBehavior] = useState(
      isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
   )
   const [searchFilters, setSearchFilters] = useState({})
   const [tableData, setTableData] = useState(VALUES.TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(VALUES.PAGING_ORDER_INITIAL_STATE)

   const [_, dispatch] = useStateValue()

   const EDITAR_SUBDOMINIO = useHasOperation(OPERACIONES_SUBDOMINIOS.EDITAR_SUBDOMINIO)

   // ---------------- Clean Form -----------------
   function cleanForm() {
      setFormData({
         ...INITIAL_SUBDOMAIN_FORM_STATE
      })
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
   }

   function clean() {
      setSearchFilters({})
      setTableData(VALUES.TABLE_DATA_INTIAL_STATE)
      setPopoverProp(true)
      setPaginationParams(VALUES.PAGING_ORDER_INITIAL_STATE)
      cleanForm()
   }

   useEffect(() => {
      clean()
   }, [isSearch]) // eslint-disable-line react-hooks/exhaustive-deps

   // --------------- Fetch all Subdomain Icons -----------------
   useEffect(() => {
      const iconsMap = {}
      iconsMaterialAPI.getAllIcons().icons.forEach(icon => (iconsMap[icon.name] = icon))
      setIconsList(iconsMap)
   }, [])

   // --------------- Search Subdomains
   function getFiltersDescriptors(filters) {
      const descriptors = buildFiltersDescriptors(options)(filters) || []
      return descriptors
   }

   async function removeSearchFilter(filter) {
      const remainingFilters = omit(searchFilters, [filter.filterName])
      await searchSubdomains(remainingFilters, VALUES.PAGING_ORDER_INITIAL_STATE)
   }

   async function _searchSubdomains(externalFilters, pagingFromTableParams) {
      const filters = externalFilters ? externalFilters : searchFilters
      const paginationFilters = {
         ...paginationParams,
         ...pagingFromTableParams
      }
      setSearchFilters(filters)
      setPaginationParams(paginationFilters)
      const data = {}
      if (filters.name) {
         data.name = filters.name
      }
      if (filters.status) {
         data.status = filters.status
      }
      const payload = { ...data, ...paginationFilters }
      const url = `${process.env.REACT_APP_SUBDOMAIN_API}/subdomains/search`
      const searchedSubdomains = await getFromApi(url, payload)
      setTableData(searchedSubdomains)
      setFormData(INITIAL_SUBDOMAIN_FORM_STATE)
   }

   const searchSubdomains = useApiRequest(_searchSubdomains, 'Subdominios: Consultar')

   // ---------- Register Subdomain ----------------
   async function _registerSubdomains() {
      const payload = new FormData()
      payload.append('name', formData.name)
      payload.append('description', formData.description)
      payload.append('icon', formData.icon)
      payload.append('status', formData.status)
      payload.append('file', formData.file)
      payload.append('map', formData.map)

      const url = `${process.env.REACT_APP_SUBDOMAIN_API}/subdomains`
      const registeredSubdomain = await postToApiFile(url, payload)
      setFormData(INITIAL_SUBDOMAIN_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [registeredSubdomain]
      })
      dispatch(openSnackBarAction(`Se guardó correctamente el Subdominio ${registeredSubdomain.name}`, 'info'))
   }

   const registerSubdomains = useApiRequest(_registerSubdomains, 'Subdominios: Registrar')

   // ---------- Edit Subdomains ------------
   async function _editSubdomains() {
      const payload = new FormData()
      payload.append('name', formData.name)
      payload.append('description', formData.description)
      payload.append('icon', formData.icon)
      payload.append('status', formData.status)
      payload.append('map', formData.map)

      if (typeof formData.file === 'object') {
         payload.append('file', formData.file)
      }

      const url = `${process.env.REACT_APP_SUBDOMAIN_API}/subdomains/${formData.id}`

      const editedSubdomain = await putToApi(url, payload)
      const formBehavior = isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
      setFormBehavior(formBehavior)
      setFormData(INITIAL_SUBDOMAIN_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [editedSubdomain]
      })
      dispatch(openSnackBarAction(`Se actualizó correctamente el Subdominio ${editedSubdomain.name}`, 'info'))
   }

   const editSubdomains = useApiRequest(_editSubdomains, 'Subdominios: Editar')

   // -------------- Form Actions ---------------
   function actionSubmit(type) {
      setPopoverProp(false)
      if (type === VALUES.FORM_BEHAVIOR.REGISTER) {
         registerSubdomains()
      }
      if (type === VALUES.FORM_BEHAVIOR.EDIT) {
         editSubdomains()
      }
      if (type === VALUES.FORM_BEHAVIOR.SEARCH) {
         searchSubdomains(formData, VALUES.PAGING_ORDER_INITIAL_STATE)
      }
   }

   function actionEdit(rowData) {
      // console.log('rowData', rowData)
      setFormBehavior(VALUES.FORM_BEHAVIOR.EDIT)
      setPopoverProp(true)
      setFormData({
         id: rowData.id,
         name: rowData.name,
         description: rowData.description,
         status: rowData.status,
         icon: rowData.icon,
         file: rowData.mutualWorld ? rowData.mutualWorld.image : 'Cargar icono Mundo Mutual*',
         map: rowData.mutualWorld?.map ? rowData.mutualWorld?.map : ''
      })
   }

   const [showError, setShowError] = useState(false)
   const [textError, setTextError] = useState('')

   const handleChangeAlert = () => {
      setAlertStatus(true)
   }

   const alertClose = () => {
      setShowError(false)
   }

   const alertOpen = () => {
      setShowError(true)
   }

   const actions = []
   if (EDITAR_SUBDOMINIO) {
      actions.push({
         icon: 'edit',
         tooltip: 'Editar subdominio',
         onClick: (_, rowData) => actionEdit(rowData)
      })
   }

   return (
      <SecurityAdministrationLayout
         alertClose={alertClose}
         showError={showError}
         textError={textError}
         title={TITLE[formBehavior]}
         form={
            <SubdomainForm
               formData={formData}
               setForm={setFormData}
               cleanCallback={cleanForm}
               actionSubmit={actionSubmit}
               formBehavior={formBehavior}
               handleChangeAlert={handleChangeAlert}
               alertOpen={alertOpen}
               setShowError={setShowError}
               setTextError={setTextError}
            />
         }
         table={
            <SubdomainTable
               isSearch={isSearch}
               order={paginationParams.order}
               subdomains={tableData}
               searchSubdomains={searchSubdomains}
               customActions={actions}
               actionEdit={actionEdit}
               cleanCallback={clean}
               iconsMap={iconsList}
            />
         }
         popoverProp={isShowingPopover}
         setPopoverProp={setPopoverProp}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
      />
   )
}

export default Subdomain
